//////////////////////////////////////////////////////////////////////////////////////
//
//  Copyright (c) 2014-present, Egret Technology.
//  All rights reserved.
//  Redistribution and use in source and binary forms, with or without
//  modification, are permitted provided that the following conditions are met:
//
//     * Redistributions of source code must retain the above copyright
//       notice, this list of conditions and the following disclaimer.
//     * Redistributions in binary form must reproduce the above copyright
//       notice, this list of conditions and the following disclaimer in the
//       documentation and/or other materials provided with the distribution.
//     * Neither the name of the Egret nor the
//       names of its contributors may be used to endorse or promote products
//       derived from this software without specific prior written permission.
//
//  THIS SOFTWARE IS PROVIDED BY EGRET AND CONTRIBUTORS "AS IS" AND ANY EXPRESS
//  OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES
//  OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED.
//  IN NO EVENT SHALL EGRET AND CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
//  INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT
//  LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;LOSS OF USE, DATA,
//  OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF
//  LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING
//  NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE,
//  EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
//
//////////////////////////////////////////////////////////////////////////////////////

let locale2 = require("locale2");

/**
 * Type of operation.
 * @version Egret 2.4
 * @platform Web,Native
 * @language en_US
 */
export class RuntimeType {
    /**
     * Running on Web
     * @version Egret 2.4
     * @platform Web,Native
     * @language en_US
     */
    public static readonly WEB: string = "web";
    /**
     * Running on NATIVE
     * @version Egret 2.4
     * @platform Web,Native
     * @language en_US
     */
    public static readonly NATIVE: string = "native";
    /**
     * Running on Unknow
     * @version Egret 2.4
     * @platform Web,Native
     * @language en_US
     */
    public static readonly UNKNOWN: string = "Unknown";
}

/**
 * Type of Operational System.
 * @version Egret 2.4
 * @platform Web,Native
 * @language en_US
 */
export class OperatingSystemType {
    public static readonly IOS: string = "iOS";
    public static readonly ANDROID: string = "Android";
    public static readonly WINDOWS_PHONE: string = "Windows Phone";
    public static readonly WINDOWS_PC: string = "Windows PC";
    public static readonly MAC_OS: string = "Mac OS";
    public static readonly LINUX: string = "Linux";
    public static readonly UNKNOW: string = "Unknown";
}

/**
 * The Capabilities class provides properties that describe the system and runtime that are hosting the application.
 * @version Egret 2.4
 * @platform Web,Native
 * @includeExample egret/system/Capabilities.ts
 * @language en_US
 */
export class Capabilities {

    /**
     * @private
     */
    static $language: string = "en-US";

    /**
     * Specifies the language code of the system on which the content is running. The language is specified as a lowercase
     * two-letter language code from ISO 639-1. For Chinese, an additional uppercase two-letter country code from ISO 3166
     * distinguishes between Simplified and Traditional Chinese.<br/>
     * The following table lists the possible values,but not limited to them:
     * <ul>
     * <li>Simplified    Chinese  zh-CN</li>
     * <li>Traditional   Chinese  zh-TW</li>
     * <li>English       en</li>
     * <li>Japanese      ja</li>
     * <li>Korean        ko</li>
     * </ul>
     * @version Egret 2.4
     * @platform Web,Native
     * @language en_US
     */
    public static get language(): string {
        return Capabilities.$language;
    }

    /**
     * @private
     */
    static $isMobile: boolean;

    /**
     * Specifies whether the system is running in a mobile device.(such as a mobile phone or tablet)
     * @version Egret 2.4
     * @platform Web,Native
     * @language en_US
     */
    public static get isMobile(): boolean {
        return Capabilities.$isMobile;
    }

    /**
     * @private
     */
    static $os: string = OperatingSystemType.UNKNOW;

    /**
     * Specifies the current operating system. The os property can return the following strings:
     * <ul>
     * <li>iPhone            OperatingSystemType.IOS</li>
     * <li>Android Phone     OperatingSystemType.ANDROID</li>
     * <li>Windows Phone     OperatingSystemType.WINDOWS_PHONE</li>
     * <li>Windows Desktop   OperatingSystemType.WINDOWS_PC</li>
     * <li>Mac Desktop       OperatingSystemType.MAC_OS</li>
     * <li>Linux Desktop     OperatingSystemType.LINUX</li>
     * <li>Unknown OS        OperatingSystemType.UNKNOW</li>
     * </ul>
     * @version Egret 2.4
     * @platform Web,Native
     * @language en_US
     */
    public static get os(): string {
        return Capabilities.$os;
    }

    /**
     * @private
     */
    static $runtimeType: string = RuntimeType.UNKNOWN;

    /**
     * It indicates the current type of operation. runtimeType property returns the following string:
     * <ul>
     * <li>Run on Web     RuntimeType.WEB</li>
     * <li>Run on Native  RuntimeType.NATIVE</li>
     * </ul>
     * @version Egret 2.4
     * @platform Web,Native
     * @language en_US
     */
    public static get runtimeType(): string {
        return Capabilities.$runtimeType;
    }

    /***
     * version of the native support.
     * @type {string}
     * @version Egret 2.5
     * @platform Web,Native
     * @language en_US
     */
    public static get supportVersion(): string {
        return Capabilities.$supportVersion;
    }

    static $supportVersion: string = "Unknown";

    /***
     * version of Egret.
     * @type {string}
     * @version Egret 3.2.0
     * @platform Web,Native
     * @language en_US
     */
    public static get engineVersion(): string {
        return "5.0.15";
    }

    public static $setNativeCapabilities(value: string): void {
        let arr = value.split("-");
        if (arr.length <= 4) {
            let osType: string = arr[0];
            switch (osType) {
                case "android":
                    osType = "Android";
                    break;
                case "ios":
                    osType = 'iOS';
                    break;
            }
            Capabilities.$os = osType;
            let version = arr[2].substring(1, arr[2].length);
            Capabilities.$supportVersion = version;
        }
    }

    /***
     * current render mode.
     * @type {string}
     * @version Egret 3.0.7
     * @platform Web,Native
     * @language en_US
     */
    public static get renderMode(): string {
        return Capabilities.$renderMode;
    }

    static $renderMode: string = "Unknown";

    /***
     * Clients border width.
     * The value before the document class initialization is always 0.
     * This value will change after the distribution Event.RESIZE and StageOrientationEvent.ORIENTATION_CHANGE.
     * @version Egret 3.1.3
     * @platform Web,Native
     * @language en_US
     */
    public static get boundingClientWidth(): number {
        return Capabilities.$boundingClientWidth;
    }

    static $boundingClientWidth: number = 0;

    /***
     * Clients border height.
     * The value before the document class initialization is always 0.
     * This value will change after the distribution Event.RESIZE and StageOrientationEvent.ORIENTATION_CHANGE.
     * @version Egret 3.1.3
     * @platform Web,Native
     * @language en_US
     */
    public static get boundingClientHeight(): number {
        return Capabilities.$boundingClientHeight;
    }

    static $boundingClientHeight: number = 0;

    public static get screenResolutionX(): number {
        return window.innerWidth || document.documentElement.clientWidth || (document.getElementsByTagName("body")[0]).clientWidth;
    }

    public static get screenResolutionY(): number {
        return window.innerHeight || document.documentElement.clientHeight || (document.getElementsByTagName("body")[0]).clientHeight;
    }

    public static get version(): string {
        return navigator.userAgent;
    }
}

/**
 * @private
 */
const detectDeviceType = (): boolean => {
    if (!window.navigator) {
        return true;
    }
    let ua: string = navigator.userAgent.toLowerCase();
    return (ua.includes("mobile") || ua.includes("android"));
};

/**
 * @private
 */
const detectRuntimeType = (): string => {
    if (window.navigator) {
        return RuntimeType.WEB;
    }
    return RuntimeType.NATIVE;
};

/**
 * @private
 */
const detectOperatingSystem = (): string => {
    let ua: string = navigator.userAgent.toLowerCase();

    if (Capabilities.$isMobile) {
        if (!ua.includes("windows") && (ua.includes("iphone") || ua.includes("ipad") || ua.includes("ipod"))) {
            return OperatingSystemType.IOS;
        }

        if (ua.includes("android") && ua.includes("linux")) {
            return OperatingSystemType.ANDROID;
        }

        if (ua.includes("windows")) {
            return OperatingSystemType.WINDOWS_PHONE;
        }
    } else {
        if (ua.includes("windows nt")) {
            return OperatingSystemType.WINDOWS_PC;
        }

        if (ua.includes("mac os")) {
            return OperatingSystemType.MAC_OS;
        }

        if (ua.includes("linux")) {
            return OperatingSystemType.LINUX;
        }
    }

    return OperatingSystemType.UNKNOW;
};

/**
 * @private
 */
let detectLanguage = (): string => {
    return typeof locale2 === "function" ? locale2() : locale2;
};

Capabilities.$isMobile = detectDeviceType();
Capabilities.$runtimeType = detectRuntimeType();
Capabilities.$os = detectOperatingSystem();
Capabilities.$language = detectLanguage();
